import React, { useCallback, useContext, useEffect, useState } from "react";
import i18next from "i18next";
import { Box, CircularProgress, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { BRAND, FONT, RADIUS, SHADOW, useLightStyles, MEDIA } from "../lightTheme";
import { Context } from "states/Store";
import {
  getFilteredContextInstances,
  isContextInstanceNameAvailable,
  getAccountAttributeById,
  getContextThirdpartyByTPid,
} from "services/edge/ContexteService";
import { getAccountAttributesByBondType } from "services/edge/AttributesService";
import { hasAttributeListItems } from "services/common/AttributeUtils";
import { AttributeType } from "services/common/Constants";
import { getDefaultValuesIfExist } from "components/thirdparties/unmanagedThirdParty/UnmanagedThirdPartyUtils";
import { formatDt } from "../utils/lightUtils";

const useStyles = makeStyles()(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    flex: 1,
  },
  body: {
    flex: 1,
    padding: "2rem 1.5rem",
    maxWidth: "960px",
    margin: "0 auto",
    width: "100%",
    boxSizing: "border-box",
  },
  card: {
    backgroundColor: BRAND.white,
    borderRadius: RADIUS.lg,
    border: `1.5px solid ${BRAND.border}`,
    boxShadow: SHADOW.soft,
    overflow: "hidden",
  },
  cols: {
    display: "grid",
    gridTemplateColumns: "1fr 1px 1fr",
    minHeight: "340px",
    "@media (max-width: 680px)": {
      gridTemplateColumns: "1fr",
    },
  },
  colLeft: {
    padding: "1.5rem",
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    minWidth: 0,
  },
  colDivider: {
    background: BRAND.border,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  orLabel: {
    position: "absolute",
    backgroundColor: BRAND.white,
    padding: "0.35rem 0.5rem",
    ...FONT.label,
    color: BRAND.textMuted,
    fontFamily: FONT.family,
    letterSpacing: "0.08em",
    border: `1px solid ${BRAND.border}`,
    borderRadius: RADIUS.md,
  },
  colRight: {
    padding: "1.5rem",
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  colTitle: {
    ...FONT.label,
    color: BRAND.textMuted,
    fontFamily: FONT.family,
    letterSpacing: "0.06em",
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
  },
  instanceList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.4rem",
    flex: 1,
    overflowY: "auto",
    maxHeight: "260px",
  },
  instanceRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.6rem 0.75rem",
    borderRadius: RADIUS.md,
    border: `1.5px solid ${BRAND.border}`,
    cursor: "pointer",
    transition: "all 0.12s",
    "&:hover": { borderColor: BRAND.primary, backgroundColor: `${BRAND.primary}05` },
  },
  instanceRowSelected: {
    borderColor: BRAND.primary,
    backgroundColor: `${BRAND.primary}08`,
  },
  instanceIcon: {
    width: "32px", height: "32px",
    borderRadius: RADIUS.sm,
    backgroundColor: BRAND.background,
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  },
  instanceName: {
    ...FONT.body,
    fontWeight: 600,
    color: BRAND.textMain,
    fontFamily: FONT.family,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  instanceSub: {
    ...FONT.meta,
    color: BRAND.textMuted,
    fontFamily: FONT.family,
  },
  fieldLabel: {
    ...FONT.label,
    color: BRAND.textMuted,
    fontFamily: FONT.family,
    marginBottom: "0.3rem",
  },
  fieldInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "0.5rem 0.75rem",
    borderRadius: RADIUS.sm,
    border: `1.5px solid ${BRAND.border}`,
    fontFamily: FONT.family,
    fontSize: "0.825rem",
    color: BRAND.textMain,
    outline: "none",
    transition: "border-color 0.15s",
    "&:focus": { borderColor: BRAND.primary },
  },
  fieldInputError: {
    borderColor: "#DC2626 !important",
  },
  footer: {
    borderTop: `1px solid ${BRAND.border}`,
    padding: "1.25rem 1.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    [MEDIA.smDown]: {
      flexDirection: "column",
      alignItems: "stretch",
      padding: "1rem",
    },
  },
  selectedBadge: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.4rem 0.75rem",
    backgroundColor: `${BRAND.primary}10`,
    border: `1px solid ${BRAND.primary}30`,
    borderRadius: RADIUS.md,
    ...FONT.small,
    color: BRAND.primary,
    fontFamily: FONT.family,
    fontWeight: 600,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  emptyText: {
    ...FONT.body,
    color: BRAND.textMuted,
    fontFamily: FONT.family,
    fontStyle: "italic",
    textAlign: "center",
    padding: "1rem 0",
  },
}));

const PAGE_SIZE = 20;

export default function StepChooseContext({ thirdparty, stepper, onSelect, onSkip, onBack }) {
  const { classes: s, cx } = useStyles();
  const { classes: light } = useLightStyles();
  const [state, dispatch] = useContext(Context);

  const contextModels = state.contextModels ?? [];
  const singleModel = contextModels.length === 1 ? contextModels[0] : null;

  // ── Left panel: search existing instances ──
  const [modelFilter, setModelFilter] = useState(singleModel?.id ?? "");
  const [search, setSearch] = useState("");
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);

  // ── Right panel: create new ──
  const [createModelId, setCreateModelId] = useState(singleModel?.id ?? "");
  const [createRef, setCreateRef] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [createExpDate, setCreateExpDate] = useState("");
  const [refError, setRefError] = useState("");
  const [checkingRef, setCheckingRef] = useState(false);
  const [createSelected, setCreateSelected] = useState(false);

  // ── Already linked contexts for this TP ──
  const [tpLinkedContextIds, setTpLinkedContextIds] = useState(new Set());
  const [loadingLinked, setLoadingLinked] = useState(false);

  useEffect(() => {
    const tpId = thirdparty?.thirdpartyId;
    if (!state.account?.id || !tpId) return;
    setLoadingLinked(true);
    getContextThirdpartyByTPid(state.account.id, tpId, dispatch)
      .then((res) => {
        const ids = new Set((res ?? []).map((ct) => String(ct.ctxInstanceId)));
        setTpLinkedContextIds(ids);
      })
      .catch(() => {})
      .finally(() => setLoadingLinked(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.account?.id, thirdparty?.thirdpartyId]);

  // Which side is "active": "existing" | "new" | null
  const hasSelection = selectedInstance || createSelected;

  // ── Fetch instances ──
  const fetchInstances = useCallback(async (mid, ref) => {
    if (!state.account?.id) return;
    setLoadingInstances(true);
    setSelectedInstance(null);
    try {
      const body = {};
      if (mid) body.modelId = parseInt(mid, 10);
      if (ref) body.reference = ref;
      const res = await getFilteredContextInstances(state.account.id, true, PAGE_SIZE, 0, body, dispatch);
      setInstances(res?.content ?? []);
    } catch (_) {
      setInstances([]);
    } finally {
      setLoadingInstances(false);
    }
  }, [state.account?.id, dispatch]);

  useEffect(() => {
    fetchInstances(modelFilter, "");
  }, [fetchInstances, modelFilter]);

  const handleApplyFilters = () => {
    fetchInstances(modelFilter, search);
  };

  const handleResetFilters = () => {
    setSearch("");
    setModelFilter(singleModel?.id ?? "");
    fetchInstances(singleModel?.id ?? "", "");
  };

  // ── Select existing ── (clears "create new" side)
  const handleSelectInstance = (inst) => {
    setSelectedInstance(inst);
    setCreateSelected(false);
    setCreateRef("");
    setRefError("");
  };

  // ── Create new ── (clears "existing" side)
  const handleCreateRef = async (ref) => {
    setCreateRef(ref);
    setRefError("");
    setCreateSelected(false);
    setSelectedInstance(null);
    if (!ref.trim() || !createModelId) return;
    setCheckingRef(true);
    try {
      const available = await isContextInstanceNameAvailable(state.account.id, ref.trim(), createModelId, dispatch);
      if (!available) {
        setRefError(i18next.t("light.contexts.refNotAvailable"));
      } else {
        setCreateSelected(true);
      }
    } catch (_) {
      setCreateSelected(true); // optimistic
    } finally {
      setCheckingRef(false);
    }
  };

  const createModelObj = contextModels.find((m) => String(m.id) === String(createModelId));

  // ── Context thirdparty attribute definitions (bondType = context_third_party) ──
  const [ctpAttrDefs, setCtpAttrDefs] = useState([]);
  const [ctpAttrValues, setCtpAttrValues] = useState({}); // { [label]: string[] }
  const [ctpAttrListItems, setCtpAttrListItems] = useState({}); // { [attrId]: string[] }

  // ── Context-level attribute definitions (bondType = context) — only filled when creating a new context ──
  const [ctxAttrDefs, setCtxAttrDefs] = useState([]);
  const [ctxAttrValues, setCtxAttrValues] = useState({});
  const [ctxAttrListItems, setCtxAttrListItems] = useState({});

  useEffect(() => {
    if (!state.account?.id) return;
    getAccountAttributesByBondType(state.account.id, "context_third_party", null)
      .then((r) => {
        const defs = r?.content ?? [];
        setCtpAttrDefs(defs);
        // Pre-fill defaults
        const defaults = {};
        defs.forEach((d) => {
          const def = getDefaultValuesIfExist(d);
          if (def?.length > 0) defaults[d.label] = def;
        });
        setCtpAttrValues(defaults);
        // Load list items for select attrs
        defs.filter((d) => hasAttributeListItems(d.type)).forEach((d) => {
          getAccountAttributeById(state.account.id, d.id)
            .then((res) => {
              const items = res.content?.values?.map((x) => x.value)?.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) ?? [];
              setCtpAttrListItems((prev) => ({ ...prev, [d.id]: items }));
            })
            .catch(() => {});
        });
      })
      .catch(() => {});

    // Load context-level attributes (for the "create new" flow)
    getAccountAttributesByBondType(state.account.id, "context", null)
      .then((r) => {
        const defs = r?.content ?? [];
        setCtxAttrDefs(defs);
        defs.filter((d) => hasAttributeListItems(d.type)).forEach((d) => {
          getAccountAttributeById(state.account.id, d.id)
            .then((res) => {
              const items = res.content?.values?.map((x) => x.value)?.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) ?? [];
              setCtxAttrListItems((prev) => ({ ...prev, [d.id]: items }));
            })
            .catch(() => {});
        });
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.account?.id]);

  const handleCtpAttrChange = (label, values) => {
    setCtpAttrValues((prev) => ({ ...prev, [label]: values }));
  };
  const handleCtxAttrChange = (label, values) => {
    setCtxAttrValues((prev) => ({ ...prev, [label]: values }));
  };

  const pendingTiersAttributes = ctpAttrDefs.map((d) => ({
    attributeId: d.id,
    label: d.label,
    values: ctpAttrValues[d.label] ?? [],
    type: d.type,
    isRequired: d.isRequired,
  }));

  // Context attributes to send to the parent when creating a new context
  const pendingContextAttributes = ctxAttrDefs.map((d) => ({
    attributeId: d.id,
    label: d.label,
    values: ctxAttrValues[d.label] ?? [],
    type: d.type,
  })).filter((a) => a.values?.length > 0 && a.values[0] !== "");

  // ── Submit ──
  const handleNext = () => {
    if (selectedInstance) {
      onSelect({ instanceId: selectedInstance.id, isNew: false, reference: selectedInstance.reference, modelName: selectedInstance.contextModelName ?? "", pendingTiersAttributes });
    } else if (createSelected) {
      onSelect({
        isNew: true,
        modelId: parseInt(createModelId, 10),
        modelName: createModelObj?.name ?? "",
        reference: createRef.trim(),
        description: createDesc.trim(),
        expirationDate: createExpDate || null,
        pendingTiersAttributes,
        contextAttributes: pendingContextAttributes,
      });
    }
  };

  return (
    <Box className={s.root}>
      {/* Stepper */}
      {stepper}

      {/* Page header */}
      <Box className={light.pageHeader} style={{ padding: "1.5rem 2rem 2rem" }}>
        <Box className={light.pageHeaderInner} style={{ maxWidth: "960px" }}>
          <Box style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <Box style={{ width: "36px", height: "36px", borderRadius: RADIUS.md, backgroundColor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <AccountTreeOutlinedIcon style={{ fontSize: "20px", color: BRAND.white }} />
            </Box>
            <Typography className={light.pageTitleWhite}>{singleModel?.name || i18next.t("light.launch.stepContextTitle")}</Typography>
          </Box>
          {thirdparty?.name && (
            <Typography className={light.pageSubtitleWhite}>{thirdparty.name}</Typography>
          )}
        </Box>
      </Box>

      {/* Body */}
      <Box className={s.body} style={{ marginTop: "-1rem" }}>
        <Box className={s.card}>
          <Box className={s.cols}>
            {/* ── Left: search existing ── */}
            <Box className={s.colLeft}>
              <Typography className={s.colTitle}>
                <SearchOutlinedIcon style={{ fontSize: "14px" }} />
                {i18next.t("light.launch.associate")}
              </Typography>

              {/* Inline search */}
              <Box style={{ display: "flex", gap: "0.5rem", alignItems: "stretch", flexWrap: "wrap" }}>
                <Box style={{ flex: "1 1 180px", display: "flex", gap: "0.4rem", alignItems: "center" }}>
                  <input
                    className={light.fieldInput}
                    placeholder={i18next.t("light.launch.searchContext")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                </Box>
                {contextModels.length > 1 && (
                  <select
                    className={light.fieldSelect}
                    value={modelFilter}
                    onChange={(e) => setModelFilter(e.target.value)}
                    style={{ flex: "0 1 200px" }}
                  >
                    <option value="">{i18next.t("light.launch.allModels")}</option>
                    {contextModels.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                )}
                <Box style={{ display: "flex", gap: "0.4rem", flexShrink: 0 }}>
                  <button className={cx(light.btnSecondary, light.btnSmall)} onClick={handleResetFilters}>{i18next.t("light.common.reset")}</button>
                  <button className={cx(light.btnPrimary, light.btnSmall)} onClick={handleApplyFilters}>{i18next.t("search")}</button>
                </Box>
              </Box>

              {/* List */}
              <Box className={s.instanceList}>
                {(loadingInstances || loadingLinked) ? (
                  <Box style={{ display: "flex", justifyContent: "center", padding: "1rem" }}>
                    <CircularProgress size={20} style={{ color: BRAND.primary }} />
                  </Box>
                ) : instances.length === 0 ? (
                  <Typography className={s.emptyText}>{i18next.t("light.launch.noContext")}</Typography>
                ) : instances.map((inst) => {
                  const isSelected = selectedInstance?.id === inst.id;
                  const isLinked = tpLinkedContextIds.has(String(inst.id));
                  return (
                    <Box
                      key={inst.id}
                      className={cx(s.instanceRow, isSelected && s.instanceRowSelected)}
                      onClick={() => !isLinked && handleSelectInstance(inst)}
                      style={isLinked ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                    >
                      <Box className={s.instanceIcon}>
                        {isSelected
                          ? <CheckCircleOutlinedIcon style={{ fontSize: "16px", color: BRAND.primary }} />
                          : <AccountTreeOutlinedIcon style={{ fontSize: "16px", color: BRAND.textMuted }} />
                        }
                      </Box>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <Typography className={s.instanceName}>{inst.reference || `#${inst.id}`}</Typography>
                          {isLinked && (
                            <span style={{
                              fontSize: "0.6rem", fontWeight: 600, fontFamily: FONT.family,
                              color: BRAND.textMuted, backgroundColor: BRAND.background,
                              padding: "0.1rem 0.4rem", borderRadius: RADIUS.sm,
                              whiteSpace: "nowrap", flexShrink: 0,
                            }}>
                              {i18next.t("light.launch.alreadyLinked")}
                            </span>
                          )}
                        </Box>
                        <Typography className={s.instanceSub}>
                          {inst.creationDate ? formatDt(inst.creationDate) : ""}
                        </Typography>
                      </Box>
                      {!isSelected && !isLinked && <ChevronRightIcon style={{ fontSize: "16px", color: BRAND.textMuted, flexShrink: 0 }} />}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* ── Divider ── */}
            <Box className={s.colDivider}>
              <span className={s.orLabel}>OU</span>
            </Box>

            {/* ── Right: create new ── */}
            <Box className={s.colRight}>
              <Typography className={s.colTitle}>
                <AddCircleOutlineIcon style={{ fontSize: "14px" }} />
                {i18next.t("light.launch.createNew")}
              </Typography>

              {/* Model selector (if multiple) */}
              {contextModels.length > 1 && (
                <Box>
                  <Typography className={s.fieldLabel}>{i18next.t("light.launch.contextModel")}</Typography>
                  <select
                    className={light.fieldSelect}
                    value={createModelId}
                    onChange={(e) => { setCreateModelId(e.target.value); setCreateRef(""); setCreateSelected(false); setRefError(""); }}
                  >
                    <option value="">{i18next.t("light.launch.selectModel")}</option>
                    {contextModels.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </Box>
              )}

              {/* Reference field */}
              <Box>
                <Typography className={s.fieldLabel}>{i18next.t("light.launch.contextRef")}</Typography>
                <Box style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <input
                    className={cx(s.fieldInput, refError && s.fieldInputError)}
                    placeholder={i18next.t("light.launch.contextRefPlaceholder")}
                    value={createRef}
                    onChange={(e) => handleCreateRef(e.target.value)}
                    disabled={!createModelId}
                  />
                  {checkingRef && (
                    <CircularProgress size={14} style={{ color: BRAND.primary, position: "absolute", right: "0.65rem" }} />
                  )}
                  {!checkingRef && createSelected && (
                    <CheckCircleOutlinedIcon style={{ fontSize: "16px", color: "#16a34a", position: "absolute", right: "0.65rem" }} />
                  )}
                </Box>
                {refError && (
                  <Typography style={{ ...FONT.small, color: "#DC2626", fontFamily: FONT.family, marginTop: "0.25rem" }}>
                    {refError}
                  </Typography>
                )}
              </Box>

              {/* Description */}
              <Box>
                <Typography className={s.fieldLabel}>{i18next.t("light.contexts.description")}</Typography>
                <textarea
                  className={s.fieldInput}
                  placeholder={i18next.t("light.contexts.description") + "…"}
                  value={createDesc}
                  onChange={(e) => setCreateDesc(e.target.value)}
                  disabled={!createModelId}
                  rows={2}
                  style={{ resize: "none", lineHeight: 1.5 }}
                />
              </Box>

              {/* Expiration date */}
              <Box>
                <Typography className={s.fieldLabel}>{i18next.t("light.contexts.colExpiration")}</Typography>
                <input
                  type="date"
                  className={s.fieldInput}
                  value={createExpDate}
                  onChange={(e) => setCreateExpDate(e.target.value)}
                  disabled={!createModelId}
                />
              </Box>

              {/* Context attributes (only when creating a new context) */}
              {ctxAttrDefs.length > 0 && (
                <Box style={{ display: "flex", flexDirection: "column", gap: "0.65rem", paddingTop: "0.4rem", borderTop: `1px solid ${BRAND.border}` }}>
                  <Typography className={s.fieldLabel} style={{ paddingTop: "0.5rem" }}>
                    {i18next.t("light.detail.attributes")}
                  </Typography>
                  {ctxAttrDefs.map((def) => {
                    const val = ctxAttrValues[def.label] ?? [];
                    const items = ctxAttrListItems[def.id] ?? [];
                    const isList = def.type === AttributeType.LIST_UNIQUE;
                    const isMulti = def.type === AttributeType.LIST_MULTIPLE;
                    const isBool = def.type === AttributeType.BOOLEAN;
                    const isDate = def.type === AttributeType.DATE;
                    return (
                      <Box key={def.id}>
                        <Typography style={{ ...FONT.label, fontSize: "0.68rem", color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.2rem" }}>
                          {def.label} {def.isRequired && <span style={{ color: BRAND.primary }}>*</span>}
                        </Typography>
                        {(isList || isBool) ? (
                          <select
                            className={light.fieldSelect}
                            style={{ width: "100%" }}
                            value={val[0] ?? ""}
                            disabled={!createModelId}
                            onChange={(e) => handleCtxAttrChange(def.label, e.target.value ? [e.target.value] : [])}
                          >
                            <option value="">—</option>
                            {isBool
                              ? [<option key="true" value="true">{i18next.t("yes")}</option>, <option key="false" value="false">{i18next.t("no")}</option>]
                              : items.map((it) => <option key={it} value={it}>{it}</option>)
                            }
                          </select>
                        ) : isMulti ? (
                          <Box style={{ display: "flex", flexDirection: "column", gap: "0.2rem", maxHeight: "100px", overflowY: "auto", padding: "0.4rem 0.5rem", border: `1.5px solid ${BRAND.border}`, borderRadius: RADIUS.sm, backgroundColor: BRAND.white }}>
                            {items.length === 0 ? <span style={{ ...FONT.small, color: BRAND.textMuted, fontStyle: "italic" }}>—</span> : items.map((it) => (
                              <label key={it} style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer", ...FONT.small, fontFamily: FONT.family }}>
                                <input type="checkbox" checked={val.includes(it)} disabled={!createModelId} onChange={(e) => { const next = e.target.checked ? [...val, it] : val.filter((v) => v !== it); handleCtxAttrChange(def.label, next); }} style={{ accentColor: BRAND.primary }} />
                                {it}
                              </label>
                            ))}
                          </Box>
                        ) : (
                          <input
                            type={isDate ? "date" : "text"}
                            className={light.fieldInput}
                            style={{ width: "100%", boxSizing: "border-box" }}
                            value={val[0] ?? ""}
                            disabled={!createModelId}
                            onChange={(e) => handleCtxAttrChange(def.label, e.target.value ? [e.target.value] : [])}
                            placeholder="—"
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

            </Box>
          </Box>

          {/* Attributs tiers du contexte */}
          {hasSelection && ctpAttrDefs.length > 0 && (
            <Box style={{ borderTop: `1px solid ${BRAND.border}`, padding: "1.25rem 1.5rem" }}>
              <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, letterSpacing: "0.06em", marginBottom: "0.75rem" }}>
                {i18next.t("light.launch.supplierAttributes")}
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "0.75rem 1.25rem" }}>
                {ctpAttrDefs.map((def) => {
                  const val = ctpAttrValues[def.label] ?? [];
                  const items = ctpAttrListItems[def.id] ?? [];
                  const isMulti = def.type === AttributeType.LIST_MULTIPLE;
                  const isList = def.type === AttributeType.LIST_UNIQUE;
                  const isBool = def.type === AttributeType.BOOLEAN;
                  const isDate = def.type === AttributeType.DATE;
                  return (
                    <Box key={def.id} style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                      <Typography style={{ ...FONT.label, fontSize: "0.68rem", color: BRAND.textMuted, fontFamily: FONT.family }}>
                        {def.label} {def.isRequired && <span style={{ color: BRAND.primary }}>*</span>}
                      </Typography>
                      {(isList || isBool) ? (
                        <select
                          value={val[0] ?? ""}
                          onChange={(e) => handleCtpAttrChange(def.label, e.target.value ? [e.target.value] : [])}
                          style={{ padding: "0.5rem 0.75rem", borderRadius: RADIUS.sm, border: `1.5px solid ${BRAND.border}`, fontFamily: FONT.family, fontSize: "0.825rem", color: BRAND.textMain, outline: "none", backgroundColor: BRAND.white }}
                        >
                          <option value="">—</option>
                          {isBool
                            ? [<option key="true" value="true">{i18next.t("yes")}</option>, <option key="false" value="false">{i18next.t("no")}</option>]
                            : items.map((it) => <option key={it} value={it}>{it}</option>)
                          }
                        </select>
                      ) : isMulti ? (
                        <Box style={{ display: "flex", flexDirection: "column", gap: "0.25rem", maxHeight: "120px", overflowY: "auto", padding: "0.4rem 0.5rem", border: `1.5px solid ${BRAND.border}`, borderRadius: RADIUS.sm, backgroundColor: BRAND.white }}>
                          {items.length === 0 ? (
                            <Typography style={{ ...FONT.small, color: BRAND.textMuted, fontStyle: "italic" }}>—</Typography>
                          ) : items.map((it) => (
                            <label key={it} style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer", ...FONT.small, fontFamily: FONT.family, color: BRAND.textMain }}>
                              <input
                                type="checkbox"
                                checked={val.includes(it)}
                                onChange={(e) => {
                                  const next = e.target.checked ? [...val, it] : val.filter((v) => v !== it);
                                  handleCtpAttrChange(def.label, next);
                                }}
                                style={{ accentColor: BRAND.primary }}
                              />
                              {it}
                            </label>
                          ))}
                        </Box>
                      ) : (
                        <input
                          type={isDate ? "date" : "text"}
                          value={val[0] ?? ""}
                          onChange={(e) => handleCtpAttrChange(def.label, e.target.value ? [e.target.value] : [])}
                          placeholder="—"
                          style={{ padding: "0.5rem 0.75rem", borderRadius: RADIUS.sm, border: `1.5px solid ${BRAND.border}`, fontFamily: FONT.family, fontSize: "0.825rem", color: BRAND.textMain, outline: "none", backgroundColor: BRAND.white, width: "100%", boxSizing: "border-box" }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Footer */}
          <Box className={s.footer}>
            <Box style={{ display: "flex", gap: "0.75rem", alignItems: "center", flex: 1, minWidth: 0 }}>
              {hasSelection && (
                <Box className={s.selectedBadge}>
                  <AccountTreeOutlinedIcon style={{ fontSize: "14px", flexShrink: 0 }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {selectedInstance
                      ? (selectedInstance.reference || `#${selectedInstance.id}`)
                      : `${i18next.t("light.launch.newContext")} — ${createModelObj?.name ?? ""} : ${createRef}`
                    }
                  </span>
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: "0.75rem", alignItems: "center", flexShrink: 0, width: { xs: "100%", sm: "auto" } }}>
              <Box
                component="button"
                className={light.btnPrimary}
                onClick={handleNext}
                disabled={!hasSelection}
                sx={{
                  opacity: hasSelection ? 1 : 0.5,
                  cursor: hasSelection ? "pointer" : "not-allowed",
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: "center",
                }}
              >
                {i18next.t("light.launch.next")} →
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
